/**
 * export-watermark.ts
 *
 * Pure helpers for the bypass-certification export watermark.
 * Extracted so they can be unit-tested without a browser or pptxgenjs.
 *
 * Rule: these helpers must never be called on a clean/certified export path.
 * They are called ONLY from the `onExportAnyway` bypass handler in each
 * generator, which means the teacher explicitly overrode a blocking or
 * warning-level certification failure.
 */

// ── Constants ────────────────────────────────────────────────────────────────

/** Exact text stamped on every bypassed export. Must match CSS content value. */
export const BYPASS_WATERMARK_TEXT = 'DRAFT — Teacher-Ready Certification Not Passed' as const;

/** CSS class added to document.body during a bypassed print. */
export const BYPASS_PRINT_CLASS = 'cert-bypass-print' as const;

// ── PPTX slide watermark ─────────────────────────────────────────────────────

/** Minimal surface of a pptxgenjs slide needed by the watermark helper. */
export interface WatermarkableSlide {
  addText(
    text: string,
    opts: {
      x: number; y: number; w: number; h: number;
      fontSize: number; bold: boolean; color: string;
      align: string; valign: string;
      rotate: number; transparency: number; charSpacing: number;
    },
  ): void;
}

/**
 * Stamps the bypass watermark onto a single pptxgenjs slide.
 * Diagonal, 60 % transparent — visible but does not cover instructional content.
 */
export function addPptxBypassWatermark(slide: WatermarkableSlide): void {
  slide.addText(BYPASS_WATERMARK_TEXT, {
    x: 0, y: 2.8, w: 10, h: 1.5,
    fontSize: 20, bold: true,
    color: 'CC0000',
    align: 'center', valign: 'middle',
    rotate: 330,       // −30 ° diagonal
    transparency: 60,  // clearly visible, does not obscure content
    charSpacing: 2,
  });
}

// ── Print / PDF watermark ────────────────────────────────────────────────────

/** Minimal surface of document.body needed for the print watermark. */
export interface WatermarkableBody {
  classList: { add(cls: string): void; remove(cls: string): void };
}

/**
 * Wraps a print invocation with the bypass body class so that the
 * `@media print { body.cert-bypass-print::before }` CSS rule fires on every page.
 *
 * @param printFn  The actual print trigger (window.print or equivalent).
 * @param isBypass When false this is a certified export — no watermark is applied.
 * @param body     Defaults to document.body; injectable for testing.
 */
export function applyPrintBypass(
  printFn: () => void,
  isBypass: boolean,
  body: WatermarkableBody = document.body,
): void {
  if (isBypass) body.classList.add(BYPASS_PRINT_CLASS);
  try {
    printFn();
  } finally {
    // Always clean up — even if printFn throws — so the DOM is never left dirty.
    if (isBypass) body.classList.remove(BYPASS_PRINT_CLASS);
  }
}
